import React, { useMemo } from 'react';
import { ChevronDown, Lock } from 'lucide-react';
import { sanitizeHtml } from '../utils/html';

export type IntroQualificationItem = {
  status: string;
  description: string;
  sequence: number;
};

export interface IntroQualificationsProps {
  items?: IntroQualificationItem[];
  onAdd?: (description: string, sequence: number) => void;
  usedSequences?: number[];
}

export default function IntroQualifications({ items, onAdd, usedSequences }: IntroQualificationsProps) {
  const bulletItems = useMemo(() => {
    const base = (items ?? [])
      .filter((i) => (i.status ?? '').toLowerCase() === 'published')
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));

    return base;
  }, [items]);

  if (!bulletItems || bulletItems.length === 0) return null;

  const isProbablyHtml = (value: string) => /<\s*[a-z][\s\S]*>/i.test(value);

  const renderDescription = (value: string) => {
    if (isProbablyHtml(value)) {
      return (
        <div
          className="[&_em]:italic [&_i]:italic [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(value) }}
        />
      );
    }

    return <div className="whitespace-pre-wrap">{value}</div>;
  };

  return (
    <div className="w-full">
      <div className="relative w-full rounded-[10px]">
        <div className="flex w-full flex-col items-start overflow-hidden rounded-[10px] border border-[#FFD230]">
          <div className="flex h-[49px] w-full items-center justify-between border-b border-solid border-[#FEE685] bg-[#FFFBEB] px-[24px]">
            <div className="flex items-center gap-[8px]">
              <Lock size={16} className="text-[#BB4D00]" />
              <div className="font-['Inter',sans-serif] text-[16px] font-medium leading-[24px] tracking-[-0.3125px] text-[#7B3306]">
                Intro Qualifications
              </div>
              <div className="rounded-[4px] bg-[#FEE685] px-[8px] py-[2px] font-['Inter',sans-serif] text-[12px] font-normal leading-[16px] text-[#973C00]">
                Internal Only
              </div>
            </div>

            <ChevronDown size={16} className="text-[#E17100]" />
          </div>

          <div className="w-full bg-white px-[24px] py-[24px]">
            <div className="flex flex-col gap-[12px]">
              {bulletItems.map((item, index) => (
                <div key={item.sequence ?? index} className="flex w-full gap-[12px]">
                  <div className="mt-[8px] size-[8px] shrink-0 rounded-full bg-[#E17100]" />
                  <div className="flex-1 font-['Inter',sans-serif] text-[14px] font-normal leading-[22.75px] tracking-[-0.1504px] text-[#364153]">
                    {renderDescription(item.description)}
                  </div>
                  {onAdd && (
                    <button
                      type="button"
                      disabled={usedSequences?.includes(item.sequence ?? index)}
                      onClick={() => onAdd(item.description, item.sequence ?? index)}
                      className="shrink-0 self-start mt-[1px] rounded border border-[#E17100] px-[8px] py-[2px] font-['Inter',sans-serif] text-[12px] font-medium text-[#E17100] hover:bg-[#FFF8F0] disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Add
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
